/**
 * Thread recovery (#5357). The hidden-history line and menu row follow the
 * server's has_hidden_history flag. Every restore is PUT /v1/thread/{id}/restore.
 */
(function () {
  'use strict';

  var UNDO_WINDOW_MS = 6000;
  var DISMISS_KEY_PREFIX = 'pm:hidden-history-dismissed:';
  var LINE_ID = 'pm-hidden-history';

  // Thread id -> has_hidden_history, from the last live-tail load of it.
  var flags = {};
  var restoring = {};

  function text(key, fallback) {
    return window.tOr('common.pm.' + key, fallback);
  }

  function activeThreadId() {
    var id = typeof window.get_active_thread_id === 'function' ? window.get_active_thread_id() : null;
    return id ? String(id) : null;
  }

  function isDismissed(threadId) {
    try {
      return window.localStorage.getItem(DISMISS_KEY_PREFIX + threadId) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberDismissed(threadId) {
    try {
      window.localStorage.setItem(DISMISS_KEY_PREFIX + threadId, '1');
    } catch (e) {
      // Blocked storage only costs the line coming back on the next open.
    }
  }

  function restore(threadId) {
    return window.apiRequestOk('/thread/' + threadId + '/restore', { method: 'PUT' }).then(function (resp) {
      // A dismissal covered the history that is now back. New hidden history gets the line again.
      try {
        window.localStorage.removeItem(DISMISS_KEY_PREFIX + threadId);
      } catch (e) {
        // Blocked storage keeps the old dismissal. The menu row still offers the restore.
      }
      return resp;
    });
  }

  function showRestoreError() {
    if (typeof window.showError === 'function') {
      window.showError(text('restore_messages_error', "Couldn't restore messages. Try again."));
    }
  }

  function el(tag, className, label) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (label != null) node.textContent = label;
    return node;
  }

  function fillIdle(line, threadId) {
    line.textContent = '';
    line.removeAttribute('aria-busy');
    line.appendChild(el('span', '', text('hidden_history_notice', 'Earlier messages are hidden.')));

    var restoreBtn = el('button', 'min-h-11 border-0 bg-transparent px-1 font-semibold text-accent-text cursor-pointer hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded sm:min-h-10', text('hidden_history_restore', 'Restore'));
    restoreBtn.type = 'button';
    restoreBtn.setAttribute('data-pm-hidden-history-restore', '');
    restoreBtn.addEventListener('click', function () { restoreHistory(threadId); });
    line.appendChild(restoreBtn);

    var dismissBtn = el('button', 'flex h-11 w-11 items-center justify-center rounded border-0 bg-transparent text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:h-10 sm:w-8');
    dismissBtn.type = 'button';
    dismissBtn.setAttribute('aria-label', window.tOr('common.close', 'Close'));
    dismissBtn.setAttribute('data-pm-hidden-history-dismiss', '');
    dismissBtn.innerHTML = '<i data-lucide="x" class="h-[15px] w-[15px]"></i>';
    dismissBtn.addEventListener('click', function () {
      rememberDismissed(threadId);
      line.remove();
    });
    line.appendChild(dismissBtn);

    if (window.LucideIcons) window.LucideIcons.initialize(line);
  }

  function fillBusy(line) {
    line.textContent = '';
    line.setAttribute('aria-busy', 'true');
    line.appendChild(el('span', 'h-3.5 w-3.5 flex-none animate-spin rounded-full border-2 border-accent-glyph border-r-transparent'));
    line.appendChild(el('span', 'py-3', text('hidden_history_restoring', 'Restoring messages…')));
  }

  function findLine() {
    return document.getElementById(LINE_ID);
  }

  /**
   * Put the line above the oldest loaded message of the open conversation,
   * or take it away. It waits until the oldest visible message is loaded,
   * because the hidden messages are older than every visible one. The paged
   * view and a date-jump slice show no line. The menu row covers them.
   */
  function place() {
    var threadId = activeThreadId();
    var line = findLine();
    var wrapper = document.getElementById('messages_wrapper');
    var show = !!threadId
      && flags[threadId] === true
      && !isDismissed(threadId)
      && !!wrapper
      && !wrapper.hasAttribute('data-paged-page')
      && !(window.calendarJump && window.calendarJump.dateJumpActive)
      && !document.getElementById('load_older_messages');

    if (!show) {
      if (line && !restoring[line.getAttribute('data-thread-id')]) line.remove();
      return;
    }
    if (line && line.getAttribute('data-thread-id') === threadId && line.parentNode === wrapper) {
      if (wrapper.firstChild !== line) wrapper.insertBefore(line, wrapper.firstChild);
      return;
    }
    if (line) line.remove();

    line = el('div', 'flex flex-wrap items-center justify-center gap-x-1 pt-1.5 text-center text-[13px] text-gray-500 dark:text-gray-400');
    line.id = LINE_ID;
    line.setAttribute('data-thread-id', threadId);
    line.setAttribute('aria-live', 'polite');
    if (restoring[threadId]) {
      fillBusy(line);
    } else {
      fillIdle(line, threadId);
    }
    wrapper.insertBefore(line, wrapper.firstChild);
  }

  function syncMenuRow() {
    var row = document.getElementById('pm-menu-restore-history');
    if (!row) return;
    var threadId = activeThreadId();
    row.hidden = !(threadId && flags[threadId] === true);
  }

  function restoreHistory(threadId) {
    threadId = String(threadId);
    if (restoring[threadId]) return;
    restoring[threadId] = true;

    var line = findLine();
    if (line && line.getAttribute('data-thread-id') === threadId) fillBusy(line);

    restore(threadId).then(function () {
      delete restoring[threadId];
      flags[threadId] = false;
      syncMenuRow();
      var current = findLine();
      if (current && current.getAttribute('data-thread-id') === threadId) current.remove();
      // The reload renders the restored messages, and its flag reads false.
      if (activeThreadId() === threadId && typeof window.view_thread === 'function') {
        window.view_thread(threadId, true);
      }
    }).catch(function () {
      delete restoring[threadId];
      var current = findLine();
      if (current && current.getAttribute('data-thread-id') === threadId) fillIdle(current, threadId);
      place();
      showRestoreError();
    });
  }

  /** Called with each live-tail load of the open conversation. */
  function applyLoad(threadId, hasHiddenHistory) {
    flags[String(threadId)] = hasHiddenHistory === true;
    syncMenuRow();
    place();
  }

  // The delete confirms in a modal and then waits on the server, so no click
  // event survives to the toast. The last input tells a keyboard delete apart.
  var lastInputKeyboard = false;

  function focusThreadList() {
    var target = document.querySelector('#threads_left a.th_snippet') || document.getElementById('threads_left');
    if (!target || typeof target.focus !== 'function') return;
    if (target.tagName !== 'A') target.setAttribute('tabindex', '-1');
    target.focus();
  }

  /** Offer to take back a conversation delete that already committed. */
  function offerUndo(threadId) {
    threadId = String(threadId);
    if (typeof window.showActionToast !== 'function') return;
    var fromKeyboard = lastInputKeyboard;
    window.showActionToast(text('thread_deleted_toast', 'Conversation deleted'), {
      actionLabel: text('thread_deleted_undo', 'Undo'),
      duration: UNDO_WINDOW_MS,
      anchor: document.getElementById('threads_left_container'),
      focusAction: fromKeyboard,
      onClose: function () {
        // The keyboard was lent to the action button. Give it back to the list.
        if (!fromKeyboard) return;
        var active = document.activeElement;
        var inToast = active && active.classList && active.classList.contains('ip-action-toast-action');
        if (!active || active === document.body || inToast) focusThreadList();
      },
      onAction: function () {
        restore(threadId).then(function () {
          // The delete guard would make the rebuild skip the returning row.
          if (window._recentlyRemoved) delete window._recentlyRemoved[threadId];
          if (typeof window.update_pm_info === 'function') window.update_pm_info(true);
        }).catch(showRestoreError);
      }
    });
  }

  function init() {
    document.addEventListener('keydown', function () { lastInputKeyboard = true; }, true);
    document.addEventListener('pointerdown', function () { lastInputKeyboard = false; }, true);

    var threadsRight = document.getElementById('threads_right');
    var threadId = activeThreadId();
    if (threadsRight && threadId && threadsRight.hasAttribute('data-has-hidden-history')) {
      applyLoad(threadId, threadsRight.getAttribute('data-has-hidden-history') === '1');
    }

    // Capture phase: the toggle's own handler in messages.js stops propagation.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('#conversation-actions-toggle')) {
        syncMenuRow();
      }
    }, true);
  }

  /** The menu row: restore the open conversation. */
  function restoreActive() {
    var threadId = activeThreadId();
    if (threadId && flags[threadId] === true) restoreHistory(threadId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PmThreadRecovery = {
    applyLoad: applyLoad,
    place: place,
    offerUndo: offerUndo,
    restoreActive: restoreActive
  };
})();
